import type { TFunction } from 'i18next';
import type { DriveUnitPortOccupancy } from '../../../redux/ApplicationSlice';
import {
  buildMechanismVariableNameByPort,
  buildMotorChartSeriesLabelByPort,
  buildOtherElectronicsVariableNameByPort,
  buildSensorVariableNameByPort,
  getConfiguredSensorClassNames,
} from '../../../utils/electronicsDashboardUtils';
import {
  chartButtonI18nKeyForStreamKey,
  getTranslationWithValues,
  isDrivebaseCsvStreamKey,
  isElectronicSensorStreamSensorKey,
  isMotorStreamSensorKey,
  isOtherElectronicsStreamSensorKey,
  sensorCheckboxI18nKey,
  streamMetricSlug,
  streamPortFromSensorKey,
} from '../../Graphs/sensor-utils';

export type CsvStreamLabelOptions = {
  /** Stream parameters registered via `register_callable` in project code. */
  customCallableParameters?: ReadonlySet<string>;
  /** Full motor port display labels (from `buildMotorChartSeriesLabelByPort`). */
  motorPortLabelByPort?: ReadonlyMap<number, string>;
  mechanismVariableNameByPort?: ReadonlyMap<number, string>;
  sensorVariableNameByPort?: ReadonlyMap<number, string>;
  otherElectronicsVariableNameByPort?: ReadonlyMap<number, string>;
};

/** `Port N: variableName` prefix for CSV export labels (mechanisms, sensors, other electronics). */
function csvPortVariableTitle(port: number, variableName: string): string {
  if (!variableName.trim()) {
    return getTranslationWithValues('dashboard.motor.portOnlyLabel', 'Port {{port}}', { port });
  }
  return getTranslationWithValues('dashboard.motor.portLabel', 'Port {{port}}: {{variableName}}', {
    port,
    variableName,
  });
}

/** `Port N: variableName metric` — shared CSV export label for mechanisms, sensors, and other electronics. */
export function csvPortMetricLabel(port: number, variableName: string, metric: string): string {
  if (!metric) return '';
  return `${csvPortVariableTitle(port, variableName)} ${metric}`;
}

function csvPortPrefixForStreamKey(dataKey: string, port: number, options?: CsvStreamLabelOptions): string {
  if (dataKey.startsWith('mechanism.')) {
    const variableName = options?.mechanismVariableNameByPort?.get(port) ?? '';
    return csvPortVariableTitle(port, variableName);
  }
  if (isMotorStreamSensorKey(dataKey)) {
    const motorPortLabel = options?.motorPortLabelByPort?.get(port);
    if (motorPortLabel) return motorPortLabel;
    const mechanismName = options?.mechanismVariableNameByPort?.get(port) ?? '';
    return csvPortVariableTitle(port, mechanismName);
  }
  if (isElectronicSensorStreamSensorKey(dataKey)) {
    const variableName = options?.sensorVariableNameByPort?.get(port) ?? '';
    return csvPortVariableTitle(port, variableName);
  }
  if (isOtherElectronicsStreamSensorKey(dataKey)) {
    const variableName = options?.otherElectronicsVariableNameByPort?.get(port) ?? '';
    return csvPortVariableTitle(port, variableName);
  }
  return csvPortVariableTitle(port, '');
}

/** Port-based motor, mechanism (`mechanism.portN.*` via `isMotorStreamSensorKey`), and sensor streams. */
function isPortBasedCsvStreamKey(dataKey: string): boolean {
  return isMotorStreamSensorKey(dataKey) || isElectronicSensorStreamSensorKey(dataKey) || isOtherElectronicsStreamSensorKey(dataKey);
}

/** Display label for a CSV export checkbox. */
export function csvStreamLabel(t: TFunction, dataKey: string, options?: CsvStreamLabelOptions): string {
  if (options?.customCallableParameters?.has(dataKey)) return dataKey;

  const port = streamPortFromSensorKey(dataKey);
  const metric = streamMetricSlug(dataKey);
  if (port !== null && isPortBasedCsvStreamKey(dataKey)) {
    const prefix = csvPortPrefixForStreamKey(dataKey, port, options);
    return metric ? `${prefix} ${metric}` : prefix;
  }

  if (isDrivebaseCsvStreamKey(dataKey)) {
    return t(sensorCheckboxI18nKey(dataKey), {
      port: streamPortFromSensorKey(dataKey) ?? '',
    });
  }

  return t(chartButtonI18nKeyForStreamKey(dataKey), dataKey);
}

function buildAllElectronicSensorVariableNameByPort(driveUnitPorts: DriveUnitPortOccupancy[], projectCode: string): Map<number, string> {
  const merged = new Map<number, string>();
  for (const sensorClassName of getConfiguredSensorClassNames(driveUnitPorts, projectCode)) {
    for (const [port, variableName] of buildSensorVariableNameByPort(driveUnitPorts, projectCode, sensorClassName)) {
      merged.set(port, variableName);
    }
  }
  return merged;
}

export function buildCsvStreamLabelOptions(
  driveUnitPorts: DriveUnitPortOccupancy[],
  projectCode: string,
  registeredCustomCallableParameters: ReadonlySet<string>
): CsvStreamLabelOptions {
  return {
    customCallableParameters: registeredCustomCallableParameters,
    motorPortLabelByPort: buildMotorChartSeriesLabelByPort(driveUnitPorts, projectCode),
    mechanismVariableNameByPort: buildMechanismVariableNameByPort(driveUnitPorts, projectCode),
    sensorVariableNameByPort: buildAllElectronicSensorVariableNameByPort(driveUnitPorts, projectCode),
    otherElectronicsVariableNameByPort: buildOtherElectronicsVariableNameByPort(driveUnitPorts, projectCode),
  };
}
